import { type RefObject, useRef } from 'react';
import { ArrowUpIcon, AudioLinesIcon, MicIcon, PaperclipIcon, PlusIcon, SquareIcon, XIcon } from 'lucide-react';
import type { SupportedRagMime } from '@shared/schemas/rag-mime-types';
import type { DocumentRevisionContextDTO } from '@shared/schemas/chat';
import { toast } from 'sonner';
import {
  PromptInput,
  PromptInputBody,
  PromptInputButton,
  PromptInputSubmit,
  PromptInputTextarea,
  type PromptInputMessage,
} from '@/components/ai-elements/prompt-input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChatWebSearchToggle } from './ChatWebSearchToggle';
import { ChatDictationBar } from './ChatDictationBar';
import { ChatDropOverlay } from './ChatDropOverlay';
import { useChatFileDrop } from '../hooks/useChatFileDrop';
import { useComposerDictation } from '../hooks/useComposerDictation';
import { getChatErrorMessage } from '../chat-errors';
import { hasPendingComposerAttachments } from '../chat-composer-state';
import { formatAudioDuration } from '../audio-transcription-ui';
import { getChatComposerAcceptAttribute } from '../chat-attachment-selection';

export type ComposerAttachment = {
  attachmentId?: string;
  downloadUrl?: string | null;
  durationMs?: number | null;
  errorCode?: string | null;
  filename?: string;
  id: string;
  kind?: 'audio' | 'document';
  mediaType: SupportedRagMime | string;
  progress?: number;
  sizeBytes?: number;
  status: 'extracting' | 'failed' | 'ready' | 'uploading' | 'validating';
  url?: string;
};

type ChatInputProps = {
  attachments: ComposerAttachment[];
  input: string;
  isComposerLocked: boolean;
  isStopRequested?: boolean;
  layout?: 'centered' | 'docked';
  lockedReason?: string;
  status: 'error' | 'ready' | 'streaming' | 'submitted';
  onCancelEditing?: () => void;
  onChangeInput: (value: string) => void;
  onFilesSelected: (files: FileList | File[]) => Promise<void>;
  onRemoveAttachment: (attachmentId: string) => void;
  onCancelDocumentRevision?: () => void;
  onSendMessage: () => Promise<void>;
  onStop: () => void;
  isWebSearchToggleDisabled?: boolean;
  onWebSearchChange?: (enabled: boolean) => void;
  showWebSearchToggle?: boolean;
  webSearchEnabled?: boolean;
  audioTranscriptionEnabled?: boolean;
  /** Composer dictation, served by its own ASR instance (independent of batch audio). */
  dictationEnabled?: boolean;
  /** Identity of the conversation draft: changing it cancels an ongoing dictation. */
  dictationScopeKey?: string;
  documentRevisionContext?: DocumentRevisionContextDTO & { title: string; version: number };
  fileInputRef?: RefObject<HTMLInputElement | null>;
};

export function ChatInput({
  attachments,
  input,
  isComposerLocked,
  isStopRequested = false,
  layout = 'docked',
  lockedReason,
  status,
  onCancelEditing,
  onChangeInput,
  onFilesSelected,
  onRemoveAttachment,
  onCancelDocumentRevision,
  onSendMessage,
  onStop,
  isWebSearchToggleDisabled = false,
  onWebSearchChange,
  showWebSearchToggle = false,
  webSearchEnabled = false,
  audioTranscriptionEnabled = false,
  dictationEnabled = false,
  dictationScopeKey,
  documentRevisionContext,
  fileInputRef: fileInputRefProp,
}: ChatInputProps) {
  const fallbackFileInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = fileInputRefProp ?? fallbackFileInputRef;
  const isSending = status === 'submitted' || status === 'streaming';

  const hasReadyAttachments = attachments.some((attachment) => attachment.status === 'ready');
  // Pending uploads still count as "sendable": the send flow queues the message
  // and waits for them to settle before dispatching (ChatGPT-like behaviour).
  const hasPendingAttachments = hasPendingComposerAttachments(attachments);
  const hasSendableContent = input.trim().length > 0 || hasReadyAttachments || hasPendingAttachments;

  const composerBodyRef = useRef<HTMLDivElement | null>(null);
  const dictation = useComposerDictation({
    composerBodyRef,
    input,
    onChangeInput,
    onError: toast.error,
    scopeKey: dictationScopeKey,
  });
  const { cancel: cancelDictation, finish: finishDictation, status: dictationStatus } = dictation;
  const isDictating = dictationStatus !== 'idle';

  async function handleSubmit(message: PromptInputMessage) {
    if (isDictating) {
      // The send path never ships a dictation: it only stops and transcribes it.
      void finishDictation();
      return;
    }

    if (isComposerLocked) {
      return;
    }

    if (!hasSendableContent && message.files.length === 0) {
      return;
    }

    try {
      await onSendMessage();
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    }
  }

  async function handleFilesSelection(files: FileList | File[]) {
    if (isComposerLocked || isDictating) {
      return;
    }

    try {
      await onFilesSelected(files);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    }
  }

  // Window-wide drop zone (also owns the drop: PromptInput's own drop listener is bypassed).
  const { isDragging } = useChatFileDrop({
    enabled: !isComposerLocked && !isDictating,
    onFiles: (files) => void handleFilesSelection(files),
  });

  const composer = (
    <PromptInput
      onKeyDown={(event) => {
        if (isSending && event.key === 'Escape') {
          event.preventDefault();
          onStop();
        }
      }}
      onSubmit={handleSubmit}
      className={[
        'rounded-[2rem] border bg-background transition-[border-color,box-shadow] dark:bg-[#2f2f2f]',
        '[&_[data-slot=input-group]]:h-auto [&_[data-slot=input-group]]:rounded-[2rem] [&_[data-slot=input-group]]:border-0 [&_[data-slot=input-group]]:bg-transparent [&_[data-slot=input-group]]:shadow-none [&_[data-slot=input-group]]:has-[[data-slot=input-group-control]:focus-visible]:border-zinc-400/55 [&_[data-slot=input-group]]:has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] [&_[data-slot=input-group]]:has-[[data-slot=input-group-control]:focus-visible]:ring-zinc-400/25 dark:[&_[data-slot=input-group]]:has-[[data-slot=input-group-control]:focus-visible]:border-zinc-500/65 dark:[&_[data-slot=input-group]]:has-[[data-slot=input-group-control]:focus-visible]:ring-zinc-500/25',
        'border-black/10 shadow-[0_6px_24px_rgba(15,23,42,0.05)] dark:border-white/10 dark:shadow-none',
        layout === 'centered' ? 'bg-background shadow-[0_12px_36px_rgba(15,23,42,0.06)] dark:bg-[#2f2f2f] dark:shadow-none' : '',
      ].join(' ')}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={getChatComposerAcceptAttribute(audioTranscriptionEnabled)}
        className="hidden"
        data-testid="chat-composer-file-input"
        onChange={(event) => {
          const files = event.target.files;

          if (files && files.length > 0) {
            void handleFilesSelection(files);
          }

          event.currentTarget.value = '';
        }}
      />

      <PromptInputBody>
        <div ref={composerBodyRef} className="flex w-full min-w-0 flex-col gap-2 p-2">
          {documentRevisionContext ? (
            <div className="flex w-full items-center justify-between gap-3 rounded-xl border border-border/70 bg-muted/35 px-3 py-2 text-xs">
              <span className="truncate font-medium">
                Modification de « {documentRevisionContext.title} » — version {documentRevisionContext.version}
              </span>
              <button
                aria-label="Annuler la modification du document"
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:bg-background hover:text-foreground"
                onClick={onCancelDocumentRevision}
                type="button"
              >
                <XIcon className="size-3.5" />
              </button>
            </div>
          ) : null}
          {attachments.length > 0 ? (
            <div className="flex w-full flex-wrap gap-2 px-2 pt-1">
              {attachments.map((attachment) => {
                const isAudio = attachment.kind === 'audio';
                const durationLabel = formatAudioDuration(attachment.durationMs);
                const statusLabel = attachment.status === 'uploading'
                  ? (typeof attachment.progress === 'number' ? `${attachment.progress} %` : 'Envoi…')
                  : attachment.status === 'validating'
                    ? 'Validation…'
                    : attachment.status === 'extracting'
                      ? 'Analyse…'
                      : attachment.status === 'failed'
                        ? 'Échec'
                        : durationLabel;
                return (
                <div
                  key={attachment.id}
                  className={[
                    'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs text-foreground',
                    attachment.status === 'failed'
                      ? 'border-destructive/40 bg-destructive/10'
                      : 'border-border/70 bg-muted/35',
                  ].join(' ')}
                >
                  {isAudio
                    ? <AudioLinesIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    : <PaperclipIcon className="size-3.5 shrink-0 text-muted-foreground" />}
                  <span className="max-w-48 truncate">
                    {attachment.filename ?? 'Fichier joint'}
                  </span>
                  {statusLabel ? (
                    <span className={attachment.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}>
                      {statusLabel}
                    </span>
                  ) : null}
                  {attachment.status === 'uploading' && typeof attachment.progress === 'number' ? (
                    <span
                      aria-valuemax={100}
                      aria-valuemin={0}
                      aria-valuenow={attachment.progress}
                      className="sr-only"
                      role="progressbar"
                    >
                      Envoi {attachment.progress} %
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(attachment.id)}
                    className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition hover:bg-background hover:text-foreground"
                    aria-label="Retirer le fichier joint"
                    disabled={isComposerLocked}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </div>
                );
              })}
            </div>
          ) : null}

          {isDictating ? (
            <ChatDictationBar
              analyser={dictation.analyser}
              onCancel={cancelDictation}
              onStop={() => void finishDictation()}
              status={dictationStatus}
            />
          ) : (
          <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-1">
            <div className="flex shrink-0 items-center gap-1 self-center">
              <PromptInputButton
                type="button"
                disabled={isComposerLocked}
                onClick={() => fileInputRef.current?.click()}
                tooltip="Ajouter un fichier"
                aria-label="Ajouter un fichier"
                className="size-9 rounded-full p-0 text-foreground/55 hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.06]"
              >
                <PlusIcon className="size-[1.25rem] stroke-[2.35]" />
              </PromptInputButton>

              {onCancelEditing ? (
                <PromptInputButton
                  type="button"
                  onClick={onCancelEditing}
                  tooltip="Annuler l’édition"
                  aria-label="Annuler l’édition"
                  className="size-9 rounded-full p-0 text-foreground/55 hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.06]"
                >
                  <XIcon className="size-[1.1rem] stroke-[2.35]" />
                </PromptInputButton>
              ) : null}
            </div>

            <div className="flex min-h-[2.25rem] min-w-0 items-center">
              <PromptInputTextarea
                aria-disabled={isComposerLocked}
                data-testid="chat-composer-input"
                value={input}
                readOnly={isComposerLocked}
                onChange={(event) => onChangeInput(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (isComposerLocked && event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                  }
                }}
                placeholder={isComposerLocked ? 'Réponse en cours…' : 'Poser une question'}
                className={[
                  'block min-h-0 w-full flex-1 resize-none border-0 bg-transparent px-1 py-0.5 align-middle !text-[1.1rem] !leading-7 text-foreground shadow-none outline-none ring-0 placeholder:text-[1.1rem] placeholder:text-foreground/55 focus-visible:ring-0',
                  isComposerLocked ? 'cursor-not-allowed opacity-70' : '',
                  layout === 'centered' ? 'max-h-36' : 'max-h-28',
                ].join(' ')}
              />
            </div>

            <div className="flex shrink-0 items-center gap-1 self-center justify-self-end">
              {showWebSearchToggle && onWebSearchChange ? (
                <span className="hidden md:inline-flex">
                  <ChatWebSearchToggle
                    disabled={isWebSearchToggleDisabled}
                    enabled={webSearchEnabled}
                    onChange={onWebSearchChange}
                  />
                </span>
              ) : null}

              {dictationEnabled && !isSending ? (
                <PromptInputButton
                  type="button"
                  disabled={isComposerLocked}
                  onClick={() => void dictation.start()}
                  tooltip="Dicter"
                  aria-label="Dicter un message"
                  data-testid="chat-dictation-button"
                  className="size-9 rounded-full p-0 text-foreground/55 hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.06]"
                >
                  <MicIcon className="size-[1.2rem] stroke-[2.2]" />
                </PromptInputButton>
              ) : null}

              <PromptInputSubmit
                status={status}
                onStop={onStop}
                disabled={isSending
                  ? isStopRequested
                  : isComposerLocked || !hasSendableContent}
                data-testid={isSending ? 'chat-stop-button' : 'chat-send-button'}
                className="size-9 rounded-full bg-foreground p-0 text-background shadow-none hover:bg-foreground/90 disabled:bg-black/[0.05] disabled:text-black/30 dark:disabled:bg-white/10 dark:disabled:text-white/30"
              >
                {isSending ? (
                  <SquareIcon className="size-[1.05rem] stroke-[2.4]" />
                ) : (
                  <ArrowUpIcon className="size-[1.1rem] stroke-[2.4]" />
                )}
              </PromptInputSubmit>
            </div>
          </div>
          )}
        </div>
      </PromptInputBody>
    </PromptInput>
  );

  const dropOverlay = <ChatDropOverlay audioEnabled={audioTranscriptionEnabled} isActive={isDragging} />;

  if (!lockedReason) {
    return (
      <>
        {composer}
        {dropOverlay}
      </>
    );
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full" data-testid="quota-locked-composer">
            {composer}
          </div>
        </TooltipTrigger>
        <TooltipContent>{lockedReason}</TooltipContent>
      </Tooltip>
      {dropOverlay}
    </>
  );
}
